<template>
  <div class="property-deduction-rebate-config">
    <el-card>
      <div slot="header">小区缴费返利配置</div>

      <div class="query-bar">
        <el-form :inline="true" :model="query" size="small">
          <el-form-item label="小区">
            <el-select v-model="query.communityId" placeholder="全部小区" clearable style="width: 220px;">
              <el-option
                v-for="item in communityOptions"
                :key="item.communityId"
                :label="item.communityName"
                :value="item.communityId"
              />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="queryConfigs(1)">查询</el-button>
            <el-button @click="resetQuery">重置</el-button>
            <el-button type="success" @click="openAddDialog">新增配置</el-button>
          </el-form-item>
        </el-form>
      </div>

      <div class="page-tip">
        <i class="el-icon-info" />
        规则说明：通用配置对所有费用类型生效；若某费用类型已设置专项配置，则优先使用专项配置。
      </div>

      <el-table :data="configs" border stripe style="width: 100%">
        <el-table-column prop="communityName" label="小区" align="center" show-overflow-tooltip>
          <template #default="{ row }">{{ row.communityName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="feeTypeCd" label="费用类型" align="center">
          <template #default="{ row }">{{ row.feeTypeName || row.feeTypeCd || '通用配置' }}</template>
        </el-table-column>
        <el-table-column prop="rebateTypeName" label="返利类型" align="center" />
        <el-table-column prop="rebateValue" label="返利值" align="center">
          <template #default="{ row }">
            {{ formatRebateValue(row) }}
          </template>
        </el-table-column>
        <el-table-column prop="maxRebate" label="单笔最高返利" align="center">
          <template #default="{ row }">{{ formatMoney(row.maxRebate) }}</template>
        </el-table-column>
        <el-table-column prop="minPayAmount" label="最低缴费金额" align="center">
          <template #default="{ row }">{{ formatMoney(row.minPayAmount) }}</template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" align="center" />
        <el-table-column label="操作" align="center" width="150">
          <template #default="{ row }">
            <el-button type="text" size="small" @click="editConfig(row)">编辑</el-button>
            <el-button type="text" size="small" style="color: #f56c6c;" @click="deleteConfig(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pagination"
        background
        layout="total, prev, pager, next"
        :current-page="query.page"
        :page-size="query.row"
        :total="total"
        @current-change="queryConfigs"
      />
    </el-card>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px" :close-on-click-modal="false">
      <el-form ref="form" :model="form" :rules="rules" label-width="120px" size="small">
        <el-form-item label="小区" prop="communityId">
          <el-select v-model="form.communityId" placeholder="请选择小区" style="width: 100%;" @change="onCommunityChange">
            <el-option
              v-for="item in communityOptions"
              :key="item.communityId"
              :label="item.communityName"
              :value="item.communityId"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="费用类型">
          <el-select
            :key="'feeType-' + feeTypeOptions.length"
            ref="feeTypeSelect"
            v-model="form.feeTypeCd"
            placeholder="空表示通用配置（所有费用类型生效）"
            clearable
            style="width: 100%;"
            :disabled="!form.communityId"
          >
            <el-option
              v-for="item in feeTypeOptions"
              :key="item.feeTypeCd + '-' + item.feeName"
              :label="item.feeName"
              :value="item.feeTypeCd"
            />
          </el-select>
          <div class="form-tip">空表示通用配置，对所有费用类型生效；若某费用类型已设置专项配置，则优先使用专项配置</div>
          <div v-if="!form.communityId" class="form-tip">请先选择小区</div>
        </el-form-item>
        <el-form-item label="返利类型" prop="rebateType">
          <el-radio-group v-model="form.rebateType">
            <el-radio label="RATIO">按比例</el-radio>
            <el-radio label="FIXED">固定金额</el-radio>
          </el-radio-group>
          <div class="form-tip">
            {{ form.rebateType === 'RATIO' ? '按比例：返利值填写小数，如 0.01 表示按缴费金额的 1% 返抵扣金' : '固定金额：返利值填写固定金额，如 1 表示每笔返 1 元抵扣金' }}
          </div>
        </el-form-item>
        <el-form-item label="返利值" prop="rebateValue">
          <el-input-number v-model="form.rebateValue" :precision="4" :min="0" :step="0.01" controls-position="right" />
          <span style="margin-left: 8px;">{{ form.rebateType === 'RATIO' ? '' : '元' }}</span>
        </el-form-item>
        <el-form-item label="单笔最高返利">
          <el-input-number v-model="form.maxRebate" :precision="2" :min="0" :step="1" controls-position="right" /> 元
        </el-form-item>
        <el-form-item label="最低缴费金额">
          <el-input-number v-model="form.minPayAmount" :precision="2" :min="0" :step="1" controls-position="right" /> 元
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="saveConfig">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { queryShopCommunity } from '@/api/admin/shopCommunityApi'
import request from '@/utils/request'

export default {
  name: 'PropertyDeductionRebateConfig',
  data() {
    return {
      query: {
        communityId: '',
        page: 1,
        row: 10
      },
      communityOptions: [],
      feeTypeOptions: [],
      configs: [],
      total: 0,
      dialogVisible: false,
      form: {
        configId: '',
        communityId: '',
        feeTypeCd: '',
        rebateType: 'RATIO',
        rebateValue: 0.01,
        maxRebate: undefined,
        minPayAmount: 0
      },
      rules: {
        communityId: [{ required: true, message: '请选择小区', trigger: 'change' }],
        rebateType: [{ required: true, message: '请选择返利类型', trigger: 'change' }],
        rebateValue: [{ required: true, message: '请输入返利值', trigger: 'blur' }]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.form.configId ? '编辑返利配置' : '新增返利配置'
    }
  },
  mounted() {
    this.loadCommunityOptions()
    this.queryConfigs(1)
  },
  methods: {
    loadCommunityOptions() {
      queryShopCommunity({ page: 1, row: 100 }).then(res => {
        const list = (res.shopCommunitys || res.data || res.communitys || []) || []
        const map = new Map()
        list.forEach(item => {
          if (item.communityId && !map.has(item.communityId)) {
            map.set(item.communityId, item)
          }
        })
        this.communityOptions = Array.from(map.values())
      }).catch(() => {
        this.$message.error('加载小区列表失败')
      })
    },
    queryConfigs(page) {
      if (page) {
        this.query.page = page
      }
      request({
        url: '/propertyDeduction.rebateConfig',
        method: 'post',
        data: {
          action: 'query',
          communityId: this.query.communityId,
          page: this.query.page,
          row: this.query.row
        }
      }).then(response => {
        const result = response.data
        const res = result.data || {}
        this.configs = (res.configs || []).map(item => ({
          ...item,
          rebateTypeName: item.rebateType === 'RATIO' ? '按比例' : '固定金额'
        }))
        this.total = res.total || res.records || this.configs.length
      }).catch(() => {
        this.$message.error('查询配置失败')
      })
    },
    resetQuery() {
      this.query.communityId = ''
      this.query.page = 1
      this.queryConfigs(1)
    },
    openAddDialog() {
      this.form = {
        configId: '',
        communityId: '',
        feeTypeCd: '',
        rebateType: 'RATIO',
        rebateValue: 0.01,
        maxRebate: undefined,
        minPayAmount: 0
      }
      this.feeTypeOptions = []
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    editConfig(row) {
      this.form = {
        configId: row.configId,
        communityId: row.communityId,
        feeTypeCd: row.feeTypeCd || '',
        rebateType: row.rebateType || 'RATIO',
        rebateValue: row.rebateValue,
        maxRebate: row.maxRebate,
        minPayAmount: row.minPayAmount || 0
      }
      this.loadFeeTypes(row.communityId)
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    loadFeeTypes(communityId) {
      if (!communityId) {
        this.feeTypeOptions = []
        return
      }
      request({
        url: '/propertyDeduction.rebateConfig',
        method: 'post',
        data: {
          action: 'queryByCommunity',
          communityId: communityId
        }
      }).then(response => {
        const result = response.data
        const res = result.data || {}
        console.log('feeTypes response:', result)
        const feeTypes = res.feeTypes || []
        // mallApi 已按 feeName 去重并过滤空字段，这里直接展示，不再按 feeTypeCd 二次去重
        this.feeTypeOptions = feeTypes
        console.log('feeTypeOptions:', this.feeTypeOptions)
        this.$nextTick(() => {
          const select = this.$refs.feeTypeSelect
          console.log('el-select options count:', select ? select.options.length : 'no ref')
        })
      }).catch(() => {
        this.feeTypeOptions = []
      })
    },
    saveConfig() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        const action = this.form.configId ? 'update' : 'save'
        request({
          url: '/propertyDeduction.rebateConfig',
          method: 'post',
          data: {
            action,
            ...this.form
          }
        }).then(() => {
          this.$message.success('保存成功')
          this.dialogVisible = false
          this.queryConfigs(1)
        }).catch(() => {
          this.$message.error('保存失败')
        })
      })
    },
    deleteConfig(row) {
      this.$confirm('确认删除该返利配置？', '提示', { type: 'warning' }).then(() => {
        request({
          url: '/propertyDeduction.rebateConfig',
          method: 'post',
          data: {
            action: 'delete',
            configId: row.configId
          }
        }).then(() => {
          this.$message.success('删除成功')
          this.queryConfigs(1)
        }).catch(() => {
          this.$message.error('删除失败')
        })
      }).catch(() => {})
    },
    onCommunityChange(communityId) {
      this.form.feeTypeCd = ''
      this.loadFeeTypes(communityId)
    },
    formatMoney(value) {
      if (value === undefined || value === null || value === '') return '-'
      return '¥' + parseFloat(value).toFixed(2)
    },
    formatRebateValue(row) {
      if (row.rebateType === 'RATIO') {
        const ratio = parseFloat(row.rebateValue) || 0
        return ratio + '（' + (ratio * 100).toFixed(2) + '%）'
      }
      return row.rebateValue + '元'
    }
  }
}
</script>

<style lang="scss" scoped>
.property-deduction-rebate-config {
  .query-bar {
    margin-bottom: 16px;
  }
  .pagination {
    margin-top: 16px;
    text-align: right;
  }
  .form-tip {
    color: #909399;
    font-size: 12px;
    line-height: 1.5;
    margin-top: 4px;
  }
  .page-tip {
    color: #606266;
    font-size: 13px;
    line-height: 1.5;
    margin-bottom: 16px;
    padding: 8px 12px;
    background-color: #f4f4f5;
    border-left: 4px solid #909399;
    border-radius: 4px;
  }
}
</style>
